import { Button, Group, Menu, Select, Text, TextInput } from '@mantine/core';
import { formatDuration, minutesToHm, suggestPiece, type MinuteRange, type ShiftPlanning } from '@reditelji/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useShiftMutations } from '../api/hooks';
import { useSession } from '../api/session';
import type { PoolVolunteer, TimelinePayload } from '../api/types';
import type { DayPhase } from '../lib/ui';
import { useUi, type FillGroup, type GapSelection } from '../state/ui';
import { Chips, GROUP_HEAD, Load, TIER_WORD } from './candidateUi';
import { DayStrip } from './DayStrip';
import {
  holderLabel,
  initials,
  pieceCandidates,
  range,
  reasonChips,
  startingPiece,
  targetFor,
  trimFor,
  type FillTarget,
  type RankedPerson,
} from './derive';
import { FreedNote, OpenShiftEditor, PostLink } from './shiftForm';
import { onControl, typingInto } from './usePlanKeys';

type Assign = (volunteer: PoolVolunteer, piece: MinuteRange & { postId: string }, narrows: boolean) => void;

/**
 * Podrobnosti for an uncovered stretch — a gap, or an open row nobody stands
 * at: the piece to fill, and who could stand there, ranked by the autofill
 * rules (SPEC §1.1B). Dodeli places them and closes the panel.
 */
export function FillDetail({
  data,
  sel,
  phase,
  planning,
  silent,
  onAssign,
}: {
  data: TimelinePayload;
  sel: GapSelection;
  phase: DayPhase;
  planning: ShiftPlanning;
  silent: boolean;
  onAssign: Assign;
}) {
  const target = targetFor(data, sel);
  if (!target) {
    return (
      <Text size="sm" c="dimmed" ta="center" py="xl">
        Tu ni več ničesar nepokritega.
      </Text>
    );
  }
  const shift =
    target.kind === 'open'
      ? data.posts.find((p) => p.id === target.postId)?.shifts.find((s) => s.id === target.shiftId)
      : undefined;
  return (
    <FillBody
      key={`${target.postId}:${target.fromMin}:${target.toMin}:${sel.fromMin}:${sel.toMin}:${shift?.updatedAt ?? ''}`}
      data={data}
      target={target}
      sel={sel}
      phase={phase}
      planning={planning}
      silent={silent}
      onAssign={onAssign}
    />
  );
}

function FillBody({
  data,
  target,
  sel,
  phase,
  planning,
  silent,
  onAssign,
}: {
  data: TimelinePayload;
  target: FillTarget;
  sel: MinuteRange;
  phase: DayPhase;
  planning: ShiftPlanning;
  silent: boolean;
  onAssign: Assign;
}) {
  const { clearSelection, setGapPiece, setAssignPreview, fillGroupsOpen, setFillGroupOpen } = useUi();
  const { update, remove } = useShiftMutations();
  // Placing somebody is a plan write (SPEC §1.1L); a finished day is archived.
  const canAct = useSession().can('plan:write') && phase !== 'past';
  const post = data.posts.find((p) => p.id === target.postId)!;
  const shift = target.kind === 'open' ? post.shifts.find((s) => s.id === target.shiftId) : undefined;
  const sectorName = data.sectors.find((s) => s.id === post.sectorId)?.name ?? '';

  // A bare gap starts from the piece autofill would cut; an open row the
  // coordinator drew is filled whole, as autofill fills it.
  const suggested = useMemo(
    () =>
      target.kind === 'open'
        ? { fromMin: target.fromMin, toMin: target.toMin }
        : suggestPiece(
            target,
            planning,
            post.type,
            data.day.key,
            post.shifts.filter((s) => s.state !== 'cancelled' && s.volunteerId !== null),
          ),
    [target, planning, post, data.day.key],
  );
  const [piece, setPiece] = useState<MinuteRange>(() => startingPiece(target, sel, suggested));
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editing, setEditing] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const narrows = piece.fromMin !== target.fromMin || piece.toMin !== target.toMin;
  const groups = useMemo(
    () => pieceCandidates(data, planning, { ...piece, postId: post.id }),
    [data, planning, piece, post.id],
  );
  const q = query.trim().toLowerCase();
  const matches = (v: PoolVolunteer) => !q || v.name.toLowerCase().includes(q);
  const rec = groups.rec.filter((c) => matches(c.person));
  const warn = groups.warn.filter((c) => matches(c.person));
  const flag = groups.flag.filter((c) => matches(c.person));
  const busy = groups.busy.filter((c) => matches(c.person));
  const teams = groups.teams.filter(matches);
  // The first group with anyone in it opens; the rest wait folded, unless the
  // coordinator opened or folded them. A search opens every group it matches in.
  const firstFull = (['rec', 'warn', 'flag'] as const).find((k) => groups[k].length > 0);
  const isOpen = (key: FillGroup) => (q !== '' ? true : (fillGroupsOpen[key] ?? key === firstFull));
  // Only what is shown takes part in ↑/↓ and ↵.
  const flat = [
    ...(isOpen('rec') ? rec : []),
    ...(isOpen('warn') ? warn : []),
    ...(isOpen('flag') ? flag : []),
  ];
  const at = Math.min(active, Math.max(0, flat.length - 1));
  const current = flat[at];

  // The piece and the highlighted candidate, drawn on the board.
  useEffect(() => {
    setGapPiece(narrows ? { ...piece, postId: post.id } : null);
  }, [narrows, piece, post.id, setGapPiece]);
  useEffect(() => {
    setAssignPreview(
      canAct && current
        ? {
            postId: post.id,
            ...piece,
            volunteerId: current.person.id,
            name: current.person.name,
            tone: current.group === 'rec' ? 'rec' : current.group === 'warn' ? 'warn' : 'flag',
          }
        : null,
    );
  }, [canAct, current, piece, post.id, setAssignPreview]);
  useEffect(
    () => () => {
      setGapPiece(null);
      setAssignPreview(null);
    },
    [setGapPiece, setAssignPreview],
  );

  const assign = (person: PoolVolunteer, range: MinuteRange) => {
    const cut = range.fromMin !== target.fromMin || range.toMin !== target.toMin;
    onAssign(person, { ...range, postId: post.id }, cut);
  };
  const choose = (c: RankedPerson) => {
    if (c.group === 'flag') setConfirming(confirming === c.person.id ? null : c.person.id);
    else assign(c.person, piece);
  };

  // ↑/↓ walk the list, ↵ takes the highlighted person, Esc closes. The search
  // box keeps them; any other field or control keeps its own keys.
  useEffect(() => {
    if (!canAct) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const inSearch = e.target === searchRef.current;
      if (typingInto(e.target) && !inSearch) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (flat.length === 0) return;
        e.preventDefault();
        setActive((i) => (Math.min(i, flat.length - 1) + (e.key === 'ArrowDown' ? 1 : flat.length - 1)) % flat.length);
      } else if (e.key === 'Enter') {
        if (!inSearch && onControl(e.target)) return;
        if (!current) return;
        e.preventDefault();
        choose(current);
      } else if (e.key === 'Escape') {
        clearSelection();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => {
    listRef.current?.querySelector('.tl-cand--active')?.scrollIntoView({ block: 'nearest' });
  }, [at]);

  // Quarter hours inside the target, and the edges in play — the target's, the
  // suggested piece's and the current piece's — which need not be on one.
  const quarterOptions = (lo: number, hi: number) => {
    const edges = [target.fromMin, target.toMin, suggested.fromMin, suggested.toMin, piece.fromMin, piece.toMin];
    const mins = new Set([lo, hi, ...edges.filter((m) => m >= lo && m <= hi)]);
    for (let m = Math.ceil(lo / 15) * 15; m <= hi; m += 15) mins.add(m);
    return [...mins].sort((x, y) => x - y).map((m) => ({ value: String(m), label: minutesToHm(m) }));
  };
  const longTarget = target.kind === 'gap' && target.toMin - target.fromMin > planning.byType[post.type].maxMin;
  const left = [
    piece.fromMin > target.fromMin ? range(target.fromMin, piece.fromMin) : null,
    piece.toMin < target.toMin ? range(piece.toMin, target.toMin) : null,
  ].filter(Boolean);
  const top = groups.rec[0];

  const card = (c: RankedPerson, index: number) => {
    const chips = reasonChips(c, data, planning, silent);
    const isActive = index === at;
    const isBusy = c.group === 'busy';
    const trim = c.group === 'flag' ? trimFor(c.person.availability, data.day.key, piece) : null;
    return (
      <div
        key={c.person.id}
        className={`tl-cand${isActive && !isBusy ? ' tl-cand--active' : ''}${isBusy ? ' tl-cand--busy' : ''}`}
        onClick={isBusy || index < 0 ? undefined : () => setActive(index)}
      >
        <div className="tl-cand-top">
          <span className="tl-avatar">{initials(c.person.name)}</span>
          <span className="tl-cand-name">{c.person.name}</span>
          {canAct && !isBusy ? (
            <Button
              size="compact-xs"
              variant={c.group === 'flag' ? 'default' : 'filled'}
              color={c.group === 'flag' ? 'red' : undefined}
              onClick={(e) => {
                e.stopPropagation();
                setActive(index);
                choose(c);
              }}
            >
              {c.group === 'flag' ? 'Dodeli…' : 'Dodeli'}
            </Button>
          ) : null}
        </div>
        <div className="tl-cand-meta">
          <span className="tl-cand-tier">{TIER_WORD[c.tier]}</span>
          <Load beforeMin={c.beforeMin} afterMin={c.afterMin} planning={planning} />
        </div>
        <DayStrip volunteer={c.person} data={data} piece={piece} bad={c.group === 'flag' || isBusy} />
        {isBusy ? (
          <Chips chips={[{ tone: 'flag', text: 'takrat že na drugem mestu' }]} />
        ) : (
          <Chips chips={chips} />
        )}
        {confirming === c.person.id ? (
          <div className="tl-cand-confirm">
            {chips
              .filter((x) => x.tone === 'flag')
              .map((x) => (
                <Text key={x.text} size="xs">
                  ▲ {x.text.charAt(0).toUpperCase() + x.text.slice(1)}.
                </Text>
              ))}
            <Group gap={6}>
              {trim ? (
                <Button size="compact-xs" onClick={() => assign(c.person, trim)}>
                  Dodeli {range(trim.fromMin, trim.toMin)}
                </Button>
              ) : null}
              <Button size="compact-xs" variant="light" color="orange" onClick={() => assign(c.person, piece)}>
                {trim ? 'Vseeno celotno' : 'Vseeno dodeli'}
              </Button>
              <Button size="compact-xs" variant="subtle" color="gray" onClick={() => setConfirming(null)}>
                Prekliči
              </Button>
            </Group>
          </div>
        ) : null}
      </div>
    );
  };

  const toggle = (key: FillGroup, label: string, count: number, dot: string, square = false) => (
    // A search shows every group it matches in, so a heading folds nothing
    // then — and records nothing that would surprise once the search is cleared.
    <button
      type="button"
      className="tl-cand-toggle"
      aria-expanded={isOpen(key)}
      disabled={q !== ''}
      onClick={() => setFillGroupOpen(key, !isOpen(key))}
    >
      <span className={`tl-pool-group-dot${square ? ' tl-pool-group-dot--square' : ''}`} style={{ background: dot }} />
      {q === '' ? <span aria-hidden="true">{isOpen(key) ? '▾' : '▸'}</span> : null} {label}
      <span className="tl-cand-count">{count}</span>
    </button>
  );

  let index = 0;
  const section = (key: 'rec' | 'warn' | 'flag', list: RankedPerson[]) =>
    list.length > 0 ? (
      <div key={key}>
        {toggle(key, GROUP_HEAD[key].label, list.length, GROUP_HEAD[key].dot)}
        {isOpen(key) ? list.map((c) => card(c, index++)) : null}
      </div>
    ) : null;

  return (
    <div className="tl-fill">
      <Group gap={8} wrap="nowrap" align="flex-start">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Group gap={8} align="center">
            <PostLink postId={post.id}>
              <Text component="span" size="lg" fw={700} style={{ fontFamily: 'var(--rr-font-mono)' }}>
                {post.code}
              </Text>
            </PostLink>
            {post.urgent ? <span className="tl-urgent">NUJEN</span> : null}
            <span className="tl-detail-when">
              {data.day.label.slice(0, 3)} {range(target.fromMin, target.toMin)} ·{' '}
              {formatDuration(target.toMin - target.fromMin)}
            </span>
          </Group>
          <PostLink postId={post.id}>
            <Text component="span" size="sm">
              {post.micro}
            </Text>
          </PostLink>
          <Text size="xs" c="dimmed">
            {sectorName} · {post.type} · {target.kind === 'open' ? 'prosta izmena' : 'nepokrito, brez izmene'}
          </Text>
        </div>
        {shift && canAct ? (
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <Button size="compact-sm" variant="default" aria-label="Dejanja izmene">
                ⋯
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                onClick={() =>
                  update.mutate({ id: shift.id, updatedAt: shift.updatedAt, state: 'cancelled', silent })
                }
              >
                Odpovej izmeno
              </Menu.Item>
              <Menu.Item color="red" onClick={() => setConfirmDelete(true)}>
                Izbriši izmeno…
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : (
          <Button size="compact-sm" variant="subtle" color="gray" aria-label="Zapri" onClick={clearSelection}>
            ✕
          </Button>
        )}
      </Group>

      {confirmDelete && shift ? (
        <div className="tl-detail-confirm">
          <Text size="xs">
            <b>
              Izbrišem izmeno {post.code} {range(shift.fromMin, shift.toMin)}?
            </b>{' '}
            Tega ni mogoče razveljaviti.
          </Text>
          <Group gap={6}>
            <Button
              size="compact-sm"
              color="red"
              variant="light"
              loading={remove.isPending}
              onClick={() => {
                remove.mutate({ id: shift.id, silent });
                clearSelection();
              }}
            >
              Izbriši
            </Button>
            <Button size="compact-sm" variant="subtle" color="gray" onClick={() => setConfirmDelete(false)}>
              Prekliči
            </Button>
          </Group>
        </div>
      ) : null}

      {shift ? <FreedNote shiftId={shift.id} data={data} /> : null}

      {shift && canAct ? (
        <>
          <button type="button" className="tl-cand-toggle" onClick={() => setEditing(!editing)}>
            {editing ? '▾' : '▸'} Uredi izmeno
          </button>
          {editing ? <OpenShiftEditor post={post} shift={shift} silent={silent} /> : null}
        </>
      ) : null}

      {canAct ? (
        <>
          <div className="tl-detail-eyebrow">ČAS DODELITVE</div>
          <div className="tl-fill-piece">
            <div className="tl-fill-bar" aria-hidden="true">
              <i
                style={{
                  left: `${((piece.fromMin - target.fromMin) / (target.toMin - target.fromMin)) * 100}%`,
                  width: `${((piece.toMin - piece.fromMin) / (target.toMin - target.fromMin)) * 100}%`,
                }}
              />
            </div>
            <Group gap={6} wrap="wrap" align="center">
              <Select
                size="xs"
                aria-label="Od"
                w={82}
                data={quarterOptions(target.fromMin, target.toMin - 15)}
                value={String(piece.fromMin)}
                allowDeselect={false}
                onChange={(v) => {
                  if (!v) return;
                  const from = Number(v);
                  setPiece({ fromMin: from, toMin: Math.max(piece.toMin, from + 15) });
                  setActive(0);
                  setConfirming(null);
                }}
              />
              <span>–</span>
              <Select
                size="xs"
                aria-label="Do"
                w={82}
                data={quarterOptions(target.fromMin + 15, target.toMin)}
                value={String(piece.toMin)}
                allowDeselect={false}
                onChange={(v) => {
                  if (!v) return;
                  const to = Number(v);
                  setPiece({ fromMin: Math.min(piece.fromMin, to - 15), toMin: to });
                  setActive(0);
                  setConfirming(null);
                }}
              />
              {longTarget ? (
                <>
                  <button
                    type="button"
                    className="tl-fill-chip"
                    aria-pressed={piece.fromMin === suggested.fromMin && piece.toMin === suggested.toMin}
                    onClick={() => {
                      setPiece(suggested);
                      setActive(0);
                    }}
                  >
                    predlagani {formatDuration(suggested.toMin - suggested.fromMin)}
                  </button>
                  <button
                    type="button"
                    className="tl-fill-chip"
                    aria-pressed={!narrows}
                    onClick={() => {
                      setPiece({ fromMin: target.fromMin, toMin: target.toMin });
                      setActive(0);
                    }}
                  >
                    celotno
                  </button>
                </>
              ) : null}
            </Group>
            <Text size="xs" c="dimmed">
              {left.length > 0 ? `Ostane nepokrito: ${left.join(', ')}.` : 'Pokrije vse.'}
            </Text>
          </div>
        </>
      ) : null}

      <div className="tl-detail-eyebrow">OSEBA</div>
      {top ? (
        <Text size="xs" c="dimmed">
          <b style={{ color: 'var(--rr-text)' }}>Predlog bi izbral {top.person.name}</b>
          {': '}
          {reasonChips(top, data, planning, silent)
            .filter((c) => c.tone === 'good' || c.tone === 'info')
            .map((c) => c.text)
            .join(', ') || 'brez opozoril'}
          .
        </Text>
      ) : null}
      <TextInput
        ref={searchRef}
        size="xs"
        mt={6}
        placeholder="Išči po imenu…"
        value={query}
        onChange={(e) => {
          setQuery(e.currentTarget.value);
          setActive(0);
        }}
      />
      <div ref={listRef}>
        {section('rec', rec)}
        {section('warn', warn)}
        {section('flag', flag)}
        {/* Nobody at all, not merely nobody in the groups that are open. */}
        {rec.length + warn.length + flag.length === 0 ? (
          <Text size="xs" c="dimmed" mt={8}>
            {q ? 'Ni zadetkov.' : 'Nihče ni prost za ta čas — skrajšaj ga ali poglej zasedene.'}
          </Text>
        ) : null}
        {busy.length > 0 ? (
          <>
            {toggle('busy', GROUP_HEAD.busy.label, busy.length, GROUP_HEAD.busy.dot)}
            {isOpen('busy') ? busy.map((c) => card(c, -1)) : null}
          </>
        ) : null}
        {teams.length > 0 ? (
          <>
            {toggle('teams', 'Zunanje ekipe', teams.length, 'oklch(0.58 0.014 90)', true)}
            {isOpen('teams')
              ? teams.map((t) => (
                  <div key={t.id} className="tl-cand">
                    <div className="tl-cand-top">
                      <span className="tl-avatar tl-avatar--team">▪</span>
                      <span className="tl-cand-name">{holderLabel(t)}</span>
                      {canAct ? (
                        <Button size="compact-xs" onClick={() => assign(t, piece)}>
                          Dodeli
                        </Button>
                      ) : null}
                    </div>
                  </div>
                ))
              : null}
          </>
        ) : null}
      </div>


      {canAct ? (
        <Text size="xs" c="dimmed" mt={10}>
          ↑↓ kandidat · ↵ dodeli · Esc zapri · N naslednja vrzel
        </Text>
      ) : null}
    </div>
  );
}
